import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { environment } from '../../../environments/environment';
import { AuthenticationService } from '../api/authentication/authentication.service';

import { jwtDecode } from 'jwt-decode';
import { Observable } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  timeout,
} from 'rxjs/operators';

@Injectable()
export class AzureTokenInterceptor implements HttpInterceptor {
  readonly authService: AuthenticationService = inject(AuthenticationService);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    const resourceNeedsToken = this.authService.resourceNeedsToken(req.url);
    if (!resourceNeedsToken) return next.handle(req);

    const currentToken = this.getValidToken(this.authService.token.getValue());
    if (currentToken) return next.handle(this.cloneRequest(req, currentToken));

    const storedToken = this.getStoredValidToken();
    if (storedToken) {
      this.authService.token.next(storedToken);
      return next.handle(this.cloneRequest(req, storedToken));
    }

    return this.authService.token.pipe(
      filter((token): token is string => !!this.getValidToken(token)),
      map((token) => this.getValidToken(token) as string),
      take(1),
      timeout(1500),
      map((token) => this.cloneRequest(req, token)),
      switchMap((request) => next.handle(request)),
      catchError(() => next.handle(req))
    );
  }

  private cloneRequest(
    req: HttpRequest<unknown>,
    token: string
  ): HttpRequest<unknown> {
    return req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private getStoredValidToken(): string | undefined {
    try {
      const auth = localStorage.getItem(`${environment.localStorageKeyPrefix}_auth`);
      if (!auth) return undefined;

      const idToken = JSON.parse(auth)?.tokenDetails?.idToken;

      return this.getValidToken(idToken);
    } catch {
      return undefined;
    }
  }

  private getValidToken(token?: string): string | undefined {
    if (!token) return undefined;

    try {
      const decoded = jwtDecode<{ exp?: number }>(token);
      if (decoded.exp && Date.now() >= decoded.exp * 1000) return undefined;

      return token;
    } catch {
      return undefined;
    }
  }
}
